import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities/api-entitites';
import {
  EntityState,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import {
  GrantProfileNotificationPreference,
  GrantProfileNotificationPreferenceUpdate,
} from '../grant-profile-types';

const preferencesAdapter = createEntityAdapter<
  GrantProfileNotificationPreference,
  string
>({
  selectId: (preference) => preference.grant_profile_id,
});

export type GrantProfileNotificationPreferenceRequestStatus =
  | 'idle'
  | 'pending'
  | 'failed';

export interface GrantProfileNotificationPreferencesState {
  preferences: EntityState<GrantProfileNotificationPreference, string>;
  readStatusByProfileId: Record<
    string,
    GrantProfileNotificationPreferenceRequestStatus
  >;
  updateStatusByProfileId: Record<
    string,
    GrantProfileNotificationPreferenceRequestStatus
  >;
  readErrorByProfileId: Record<string, ApiError | undefined>;
  updateErrorByProfileId: Record<string, ApiError | undefined>;
}

const initialState: GrantProfileNotificationPreferencesState = {
  preferences: preferencesAdapter.getInitialState(),
  readStatusByProfileId: {},
  updateStatusByProfileId: {},
  readErrorByProfileId: {},
  updateErrorByProfileId: {},
};

const getApiError = (error: unknown): ApiError => {
  const responseData = (error as { response?: { data?: ApiError } } | undefined)
    ?.response?.data;
  return (
    responseData ?? {
      error: error instanceof Error ? error.message : 'Unexpected API error',
      error_code: 0,
    }
  );
};

export const fetchGrantProfileNotificationPreference = createAsyncThunk<
  GrantProfileNotificationPreference,
  { api: ClientApiV2; grantProfileId: string },
  { rejectValue: ApiError }
>(
  'client/grantProfileNotificationPreferences/fetchOne',
  async ({ api, grantProfileId }, { rejectWithValue }) => {
    try {
      return (
        await api.get<GrantProfileNotificationPreference>(
          `/grant-profiles/${grantProfileId}/notification-preferences`,
        )
      ).data;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

export const updateGrantProfileNotificationPreference = createAsyncThunk<
  GrantProfileNotificationPreference,
  {
    api: ClientApiV2;
    grantProfileId: string;
    request: GrantProfileNotificationPreferenceUpdate;
  },
  { rejectValue: ApiError }
>(
  'client/grantProfileNotificationPreferences/update',
  async ({ api, grantProfileId, request }, { rejectWithValue }) => {
    try {
      return (
        await api.put<GrantProfileNotificationPreference>(
          `/grant-profiles/${grantProfileId}/notification-preferences`,
          request,
        )
      ).data;
    } catch (error) {
      return rejectWithValue(getApiError(error));
    }
  },
);

const grantProfileNotificationPreferencesSlice = createSlice({
  name: 'grantProfileNotificationPreferences',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchGrantProfileNotificationPreference.pending,
        (state, { meta }) => {
          const { grantProfileId } = meta.arg;
          state.readStatusByProfileId[grantProfileId] = 'pending';
          state.readErrorByProfileId[grantProfileId] = undefined;
        },
      )
      .addCase(
        fetchGrantProfileNotificationPreference.fulfilled,
        (state, { payload }) => {
          preferencesAdapter.upsertOne(state.preferences, payload);
          state.readStatusByProfileId[payload.grant_profile_id] = 'idle';
          state.readErrorByProfileId[payload.grant_profile_id] = undefined;
        },
      )
      .addCase(
        fetchGrantProfileNotificationPreference.rejected,
        (state, { meta, payload }) => {
          const { grantProfileId } = meta.arg;
          state.readStatusByProfileId[grantProfileId] = 'failed';
          state.readErrorByProfileId[grantProfileId] =
            payload ?? ApiErrorInitialState;
        },
      )
      .addCase(
        updateGrantProfileNotificationPreference.pending,
        (state, { meta }) => {
          const { grantProfileId } = meta.arg;
          state.updateStatusByProfileId[grantProfileId] = 'pending';
          state.updateErrorByProfileId[grantProfileId] = undefined;
        },
      )
      .addCase(
        updateGrantProfileNotificationPreference.fulfilled,
        (state, { payload }) => {
          preferencesAdapter.upsertOne(state.preferences, payload);
          state.updateStatusByProfileId[payload.grant_profile_id] = 'idle';
          state.updateErrorByProfileId[payload.grant_profile_id] = undefined;
        },
      )
      .addCase(
        updateGrantProfileNotificationPreference.rejected,
        (state, { meta, payload }) => {
          const { grantProfileId } = meta.arg;
          state.updateStatusByProfileId[grantProfileId] = 'failed';
          state.updateErrorByProfileId[grantProfileId] =
            payload ?? ApiErrorInitialState;
        },
      );
  },
});

export const {
  selectAll: selectAllGrantProfileNotificationPreferences,
  selectById: selectGrantProfileNotificationPreferenceById,
} = preferencesAdapter.getSelectors(
  (state: RootState) => state.grantProfileNotificationPreferences.preferences,
);

export const selectGrantProfileNotificationPreferenceEntities = (
  state: RootState,
) => state.grantProfileNotificationPreferences.preferences.entities;

export const selectGrantProfileNotificationPreferenceReadStatuses = (
  state: RootState,
) => state.grantProfileNotificationPreferences.readStatusByProfileId;

export const selectGrantProfileNotificationPreferenceUpdateStatuses = (
  state: RootState,
) => state.grantProfileNotificationPreferences.updateStatusByProfileId;

export const selectGrantProfileNotificationPreferenceReadErrors = (
  state: RootState,
) => state.grantProfileNotificationPreferences.readErrorByProfileId;

export const selectGrantProfileNotificationPreferenceUpdateErrors = (
  state: RootState,
) => state.grantProfileNotificationPreferences.updateErrorByProfileId;

export const grantProfileNotificationPreferencesReducer =
  grantProfileNotificationPreferencesSlice.reducer;
